import azureClient from './azureClient';

/**
 * User Registry API Service
 * Manages user registration, login tracking, and admin privileges
 */

export const userRegistryApi = {
  // Local cache for users to prevent repeated scans
  _usersCache: null,
  _cacheTimestamp: 0,
  _CACHE_TTL: 5 * 60 * 1000, // 5 minutes

  /**
   * Clear the local users cache
   */
  clearCache() {
    this._usersCache = null;
    this._cacheTimestamp = 0;
  },

  /**
   * Register or update user on successful login
   * Uses associations container with record type filtering
   * @param {Object} userInfo - User information
   * @param {string} userInfo.name - User's display name
   * @param {string} userInfo.email - User's email address
   * @param {boolean} userInfo.isAdmin - Whether user has admin privileges
   */
  async registerOrUpdateUser(userInfo) {
    try {
      const { name, email, isAdmin = false } = userInfo;

      console.log('📊 User Registry - registerOrUpdateUser:', { name, email, isAdmin });

      if (!name || !email) {
        throw new Error('Name and email are required');
      }

      // Check if user already exists
      const existingUser = await this.getUserByEmail(email);

      const loginRecord = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      if (existingUser) {
        // Check if the existing user is marked as deleted
        if (existingUser.deleted === true || existingUser.status === 'DELETED_USER_RECORD') {
          console.log('⚠️ Found deleted user record, creating new user instead');
          // Fall through to new user creation below
        } else {
          console.log('🔄 Updating existing user');
          // Update existing user with new login
          // ONLY send fields that need updating to avoid Cosmos DB patch limit (max 10 ops)
          // and to avoid sending system fields (_ts, _etag) which cause 500 errors
          const updates = {
            name, // Update name in case it changed
            isAdmin,
            lastLogin: loginRecord.timestamp,
            loginHistory: [loginRecord, ...(existingUser.loginHistory || [])].slice(0, 50), // Keep last 50 logins
          };

          try {
            await azureClient.post(
              `/update?containerId=associations&databaseId=condoshield&id=${existingUser.id}`,
              updates
            );

            this.clearCache();
            console.log('✅ User updated successfully');
            return { ...existingUser, ...updates };
          } catch (updateError) {
            // If update fails (e.g., 500 error on deleted record), create new user
            if (updateError.response?.status === 500) {
              console.error('⚠️ Update failed with 500:', updateError.message);
              // Do NOT automatically create a new user on 500, as this causes infinite loops
              // if the error is due to payload issues (like system fields) rather than missing records.
              // Instead, throw the error so the UI knows something is wrong.
              throw updateError;
            } else {
              throw updateError;
            }
          }
        }
      }

      // Create new user (either no existing user found, user was deleted, or update failed)
      console.log('🆕 Creating new user');
      const newUser = {
        name,
        email: email.toLowerCase(),
        isAdmin,
        createdAt: new Date().toISOString(),
        lastLogin: loginRecord.timestamp,
        loginHistory: [loginRecord],
        status: 'active',
        recordType: 'user', // Special flag to identify user records
        associationName: `User: ${name}`, // Required field for associations container
        associationType: 'USER_ACCOUNT', // Special type for user accounts
      };

      const response = await azureClient.post(
        '/save?containerId=associations&databaseId=condoshield',
        newUser
      );

      this.clearCache();
      console.log('✅ New user created successfully');
      return response.data;
    } catch (error) {
      console.error('User registry error:', error);
      // Re-throw with a more user-friendly message
      throw new Error(`User registry service unavailable: ${error.message}`);
    }
  },

  /**
   * Get user by email address
   * @param {string} email - User's email address
   * @returns {Object|null} User object or null if not found
   */
  async getUserByEmail(email) {
    const targetEmail = email.toLowerCase();
    
    try {
      // Use cached getAllUsers for efficiency
      const allUsers = await this.getAllUsers();

      const records = allUsers.filter(c => 
        c.email && 
        c.email.toLowerCase() === targetEmail
      );
      
      if (!records || records.length === 0) {
        return null;
      }

      // If duplicates exist, sort by timestamp (newest first) to get the latest working record
      // _ts is Cosmos DB timestamp (seconds), createdAt is ISO string
      records.sort((a, b) => {
          const tsA = a._ts || (a.createdAt ? new Date(a.createdAt).getTime() / 1000 : 0);
          const tsB = b._ts || (b.createdAt ? new Date(b.createdAt).getTime() / 1000 : 0);
          return tsB - tsA;
      });

      if (records.length > 1) {
          console.warn(`getUserByEmail: Found ${records.length} duplicate users for ${targetEmail}. Using newest: ${records[0].id}`);
      }

      return records[0];

    } catch (error) {
      console.error(`Error finding user by email ${targetEmail}:`, error);
      return null;
    }
  },

  /**
   * Get all users (admin only)
   * @returns {Array} Array of user objects
   */
  async getAllUsers() {
    try {
      // Check cache first
      if (this._usersCache && (Date.now() - this._cacheTimestamp < this._CACHE_TTL)) {
        return this._usersCache;
      }

      // Use cached paginated helper
      const allRecords = await azureClient.getPaginatedAssociations({ 
        containerId: 'associations' 
      });
      
      const users = allRecords.filter(c => c.recordType === "user" && c.deleted !== true);
      
      // Update cache
      this._usersCache = users;
      this._cacheTimestamp = Date.now();
      
      return users;

    } catch (error) {
      console.error('Error fetching all users:', error);
      return this._usersCache || []; // Return stale cache if error
    }
  },

  /**
   * Update user admin status
   * @param {string} userId - User ID
   * @param {boolean} isAdmin - Admin status
   */
  async updateUserAdminStatus(userId, isAdmin) {
    try {
      const updates = {
        isAdmin,
        updatedAt: new Date().toISOString(),
      };

      await azureClient.post(
        `/update?containerId=associations&databaseId=condoshield&id=${userId}`,
        updates
      );

      return true;
    } catch (error) {
      console.error('Error updating user admin status:', error);
      throw error;
    }
  },

  /**
   * Get user statistics
   * @returns {Object} User statistics
   */
  async getUserStats() {
    try {
      const users = await this.getAllUsers();

      const stats = {
        totalUsers: users.length,
        adminUsers: users.filter(u => u.isAdmin).length,
        activeUsers: users.filter(u => u.status !== 'inactive').length, // Default to active if no status
        recentLogins: users.filter(u => {
          const lastLogin = new Date(u.lastLogin || u.createdAt);
          const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return lastLogin > weekAgo;
        }).length,
      };

      console.log('📊 User stats calculated:', stats);
      return stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {
        totalUsers: 0,
        adminUsers: 0,
        activeUsers: 0,
        recentLogins: 0,
      };
    }
  },

  /**
   * Clean up duplicate user records (admin only)
   * @returns {Object} Cleanup results
   */
  async cleanupDuplicateUsers() {
    try {
      const response = await azureClient.get(
        '/getAll?databaseId=condoshield&containerId=associations'
      );

      const allRecords = response.data || [];
      const userRecords = allRecords.filter(record => record.recordType === 'user');

      // Group users by email
      const userGroups = new Map();
      userRecords.forEach(user => {
        const email = user.email?.toLowerCase();
        if (!email) return;

        if (!userGroups.has(email)) {
          userGroups.set(email, []);
        }
        userGroups.get(email).push(user);
      });

      let duplicatesRemoved = 0;
      let totalProcessed = 0;

      // Process each group of users with the same email
      for (const [email, users] of userGroups) {
        if (users.length > 1) {
          totalProcessed++;
          console.log(`🔧 Processing ${users.length} duplicate records for ${email}`);

          // Sort by most recent activity
          users.sort(
            (a, b) => new Date(b.lastLogin || b.createdAt) - new Date(a.lastLogin || a.createdAt)
          );

          const primaryUser = users[0];
          const duplicateUsers = users.slice(1);

          // Merge all login history into the primary user
          const allLoginHistory = users
            .flatMap(u => u.loginHistory || [])
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 50); // Keep last 50 logins

          // Update primary user with merged data
          const mergedUser = {
            ...primaryUser,
            loginHistory: allLoginHistory,
            updatedAt: new Date().toISOString(),
          };

          // Update the primary record
          await azureClient.post(
            `/update?containerId=associations&databaseId=condoshield&id=${primaryUser.id}`,
            mergedUser
          );

          // Delete duplicate records
          for (const duplicate of duplicateUsers) {
            await azureClient.delete(
              `/delete?containerId=associations&databaseId=condoshield&id=${duplicate.id}`
            );
            duplicatesRemoved++;
          }

          console.log(
            `✅ Merged ${users.length} records for ${email}, removed ${duplicateUsers.length} duplicates`
          );
        }
      }

      return {
        totalProcessed,
        duplicatesRemoved,
        message: `Processed ${totalProcessed} users, removed ${duplicatesRemoved} duplicate records`,
      };
    } catch (error) {
      console.error('Error cleaning up duplicate users:', error);
      throw new Error(`Cleanup failed: ${error.message}`);
    }
  },

  /**
   * Save user profile settings (userSignature, bookingLink, etc.)
   * @param {string} userEmail - User's email address
   * @param {Object} profileSettings - Profile settings to save
   */
  async saveUserProfile(userEmail, profileSettings) {
    try {
      console.log('💾 Saving user profile settings:', { userEmail, profileSettings });

      const user = await this.getUserByEmail(userEmail);
      if (!user) {
        console.error('User not found for email:', userEmail);
        throw new Error('User account not found. Please log out and log back in.');
      }

      console.log('📝 Found user:', { id: user.id, name: user.name, email: user.email });

      // Merge new profile settings with existing ones
      const updatedProfileSettings = {
        ...(user.profileSettings || {}),
        ...profileSettings,
        updatedAt: new Date().toISOString(),
      };

      // Use PATCH operation to only update profileSettings field (avoids exceeding 10 operation limit)
      // Create minimal update object with only the fields that need to change
      const minimalUpdate = {
        id: user.id,
        profileSettings: updatedProfileSettings,
      };

      console.log('🔄 Updating user profile settings (minimal patch)...');

      const response = await azureClient.post(
        `/update?containerId=associations&databaseId=condoshield&id=${user.id}`,
        minimalUpdate
      );

      console.log('✅ User profile settings saved successfully');
      
      // Trigger storage event for other tabs/components to reload
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('userProfileUpdated', { 
          detail: updatedProfileSettings 
        }));
      }

      return updatedProfileSettings;
    } catch (error) {
      console.error('❌ Error saving user profile settings:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      
      // Check if it's the patch operations limit error
      if (error.response?.data?.Errors?.some(e => e.includes('patch operations'))) {
        throw new Error('Profile data too large. Please contact support.');
      }
      
      throw new Error(`Failed to save profile settings: ${error.message}`);
    }
  },

  /**
   * Get user profile settings (userSignature, bookingLink, etc.)
   * @param {string} userEmail - User's email address
   */
  async getUserProfile(userEmail) {
    try {
      console.log('📋 Getting user profile settings for:', userEmail);

      const user = await this.getUserByEmail(userEmail);
      if (!user) {
        console.warn('User not found for profile settings (returning defaults):', userEmail);
        return {};
      }

      console.log('📋 User found, profileSettings:', user.profileSettings);
      console.log('📋 Full user object keys:', Object.keys(user));

      return user.profileSettings || {};
    } catch (error) {
      console.error('Error getting user profile settings:', error);
      // Return empty object instead of throwing to prevent UI crashes
      return {};
    }
  },

  /**
   * Save company-wide settings (companyName, companyPhone, etc.)
   * @param {Object} companySettings - Company settings to save
   */
  async saveCompanySettings(companySettings) {
    try {
      console.log('🏢 Saving company settings:', companySettings);

      const settingsId = 'company-settings-global';
      const settingsData = {
        ...companySettings,
        updatedAt: new Date().toISOString(),
      };

      // Helper to find record by ID with pagination
      const findRecord = async () => {
        let page = 1;
        const pageSize = 100;
        while (true) {
          try {
            const response = await azureClient.get('/getAll', {
              params: { databaseId: 'condoshield', containerId: 'associations', page, pageSize }
            });
            const payload = response && response.data ? response.data : response;
            let records = [];
            if (Array.isArray(payload)) records = payload;
            else if (payload && typeof payload === 'object') records = payload.documents || payload.resources || payload.items || payload.value || [];
            
            if (records.length === 0) break;
            const match = records.find(r => r.id === settingsId);
            if (match) return match;
            if (records.length < pageSize) break;
            page++;
            if (page > 100) break;
          } catch (e) {
            console.error('Error scanning for company settings:', e);
            break;
          }
        }
        return null;
      };

      const existingRecord = await findRecord();

      if (existingRecord) {
        // Update existing company settings
        // ONLY send the field we want to update to avoid 500 errors with system fields
        const updates = {
          companySettings: settingsData,
        };

        await azureClient.post(
          `/update?containerId=associations&databaseId=condoshield&id=${settingsId}`,
          updates
        );
      } else {
        // Create new company settings record
        const newRecord = {
          id: settingsId,
          recordType: 'company_settings',
          associationName: 'Company Settings',
          associationType: 'COMPANY_SETTINGS',
          companySettings: settingsData,
          createdAt: new Date().toISOString(),
        };

        await azureClient.post(
          '/save?containerId=associations&databaseId=condoshield',
          newRecord
        );
      }

      console.log('✅ Company settings saved successfully');
      return settingsData;
    } catch (error) {
      console.error('Error saving company settings:', error);
      throw new Error(`Failed to save company settings: ${error.message}`);
    }
  },

  /**
   * Get company-wide settings
   */
  async getCompanySettings() {
    try {
      console.log('🏢 Getting company settings');

      const settingsId = 'company-settings-global';
      
      // Scan pages to find the settings record
      let page = 1;
      const pageSize = 100;
      let existingRecord = null;

      while (true) {
        try {
          const response = await azureClient.get('/getAll', {
            params: {
              databaseId: 'condoshield',
              containerId: 'associations',
              page,
              pageSize
            },
          });

          // Normalize response
          const payload = response && response.data ? response.data : response;
          let records = [];
          if (Array.isArray(payload)) {
            records = payload;
          } else if (payload && typeof payload === 'object') {
            records = payload.documents || payload.resources || payload.items || payload.value || [];
          }
          records = Array.isArray(records) ? records : [];

          if (records.length === 0) break;

          existingRecord = records.find(record => record.id === settingsId);
          if (existingRecord) break;

          if (records.length < pageSize) break;
          page++;
          if (page > 100) break;
        } catch (e) {
          console.error('Error fetching page for company settings:', e);
          break;
        }
      }

      if (existingRecord && existingRecord.companySettings) {
        return existingRecord.companySettings;
      }

      return {}; // Return empty object if no settings found
    } catch (error) {
      console.error('Error getting company settings:', error);
      throw new Error(`Failed to get company settings: ${error.message}`);
    }
  },
};

export default userRegistryApi;
